/**
 * ExerciseLibraryUI — composants de présentation extraits de ExerciseLibrary.jsx
 * (badges, image/vidéo, modal détail, autocomplete) pour garder le fichier principal
 * plus court. Aucune logique modifiée — extraction mécanique pure.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, X, Loader2, Dumbbell, Zap, Sparkles,
  ImageOff, Clapperboard, ThumbsUp, ThumbsDown, Play, Activity, Trash2, BadgeCheck,
  Shield, Layers, Triangle, Mountain, Hand, Grid3x3, Waves, CircleDot, TrendingUp,
  TrendingDown, ChevronsUp, Move, Flame, Wallet, History, AlertTriangle, Save,
} from 'lucide-react';
import { ExerciseAdminTools } from './ExerciseAdminTools';
import { toast } from 'sonner';

export const LEVEL_COLORS = {
  debutant: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  intermediaire: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  avance: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  pro: 'bg-pink-500/15 text-pink-300 border-pink-500/30',
};

export const EQUIPMENT_LABEL_FR = {
  machine_guidee: 'Machine guidée',
  poulie: 'Poulie',
  barre_libre: 'Barre libre',
  halteres: 'Haltères',
  poids_du_corps: 'Poids du corps',
  petit_materiel: 'Petit matériel',
};
export const EQUIPMENT_LABEL_EN = {
  machine_guidee: 'Guided machine',
  poulie: 'Cable pulley',
  barre_libre: 'Barbell',
  halteres: 'Dumbbells',
  poids_du_corps: 'Bodyweight',
  petit_materiel: 'Small equipment',
};

export const EQUIPMENT_ICON = { poids_du_corps: Zap };

/** Icône + couleur distincte par groupe musculaire — pour un filtre visuel rapide à scanner
 * (façon "pictogramme"), sans utiliser de caractères emoji unicode (rendu incohérent). */
export const MUSCLE_GROUP_STYLE = {
  'Pectoraux': { icon: Shield, color: 'text-rose-300 border-rose-500/40 bg-rose-500/10', activeColor: 'from-rose-500 to-pink-500' },
  'Dos': { icon: Layers, color: 'text-blue-300 border-blue-500/40 bg-blue-500/10', activeColor: 'from-blue-500 to-indigo-500' },
  'Épaules': { icon: Triangle, color: 'text-amber-300 border-amber-500/40 bg-amber-500/10', activeColor: 'from-amber-500 to-orange-500' },
  'Trapèzes': { icon: Mountain, color: 'text-stone-300 border-stone-500/40 bg-stone-500/10', activeColor: 'from-stone-500 to-neutral-500' },
  'Biceps': { icon: Dumbbell, color: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', activeColor: 'from-emerald-500 to-teal-500' },
  'Triceps': { icon: Dumbbell, color: 'text-teal-300 border-teal-500/40 bg-teal-500/10', activeColor: 'from-teal-500 to-cyan-500' },
  'Avant-bras': { icon: Hand, color: 'text-orange-300 border-orange-500/40 bg-orange-500/10', activeColor: 'from-orange-500 to-amber-500' },
  'Abdominaux': { icon: Grid3x3, color: 'text-yellow-300 border-yellow-500/40 bg-yellow-500/10', activeColor: 'from-yellow-500 to-lime-500' },
  'Lombaires': { icon: Waves, color: 'text-indigo-300 border-indigo-500/40 bg-indigo-500/10', activeColor: 'from-indigo-500 to-violet-500' },
  'Fessiers': { icon: CircleDot, color: 'text-pink-300 border-pink-500/40 bg-pink-500/10', activeColor: 'from-pink-500 to-fuchsia-500' },
  'Quadriceps': { icon: TrendingUp, color: 'text-sky-300 border-sky-500/40 bg-sky-500/10', activeColor: 'from-sky-500 to-blue-500' },
  'Ischio-jambiers': { icon: TrendingDown, color: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10', activeColor: 'from-cyan-500 to-sky-500' },
  'Mollets': { icon: ChevronsUp, color: 'text-lime-300 border-lime-500/40 bg-lime-500/10', activeColor: 'from-lime-500 to-emerald-500' },
  'Adducteurs / Abducteurs': { icon: Move, color: 'text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10', activeColor: 'from-fuchsia-500 to-purple-500' },
  'Cardio': { icon: Flame, color: 'text-red-300 border-red-500/40 bg-red-500/10', activeColor: 'from-red-500 to-orange-500' },
};

/** Pastille "pictogramme" pour un groupe musculaire — icône colorée + libellé, pensée pour un
 * scan visuel rapide (remplace les longues rangées de FilterChip textuels génériques). */
export const MuscleGroupChip = ({ group, active, onClick, testid }) => {
  const style = MUSCLE_GROUP_STYLE[group] || { icon: Dumbbell, color: 'text-zinc-300 border-white/10 bg-zinc-800/60', activeColor: 'from-primary to-pink-500' };
  const Icon = style.icon;
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex flex-col items-center gap-1 px-2.5 py-2 rounded-2xl border transition-all shrink-0 ${
        active ? `bg-gradient-to-br ${style.activeColor} text-white border-transparent shadow-lg scale-105` : `${style.color} hover:scale-105`
      }`}>
      <Icon className="w-4 h-4" />
      <span className="text-[9px] font-bold leading-none whitespace-nowrap">{group}</span>
    </button>
  );
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export const isRecentAiExercise = (ex) => {
  if (!ex?.created_by_ai || !ex?.created_at) return false;
  const created = new Date(ex.created_at).getTime();
  return !Number.isNaN(created) && (Date.now() - created) < 7 * 24 * 60 * 60 * 1000;
};

export const isRecentVideo = (ex) => {
  if (!ex?.video_updated_at || ex.video_status !== 'ready') return false;
  const t = new Date(ex.video_updated_at).getTime();
  return !Number.isNaN(t) && (Date.now() - t) < 7 * 24 * 60 * 60 * 1000;
};

/** Statut de relecture admin — purement informatif (ne change jamais la visibilité côté
 * client, déjà live immédiatement dès la génération). 'draft' = du contenu IA a été
 * (re)généré depuis la dernière relecture admin ; 'reviewed' = tout le contenu actuel a été
 * relu ; null = pas de contenu généré tracé (ancien exercice, rien à signaler). */
export const getReviewStatus = (ex) => {
  const timestamps = [ex?.image_updated_at, ex?.video_updated_at, ex?.execution_updated_at]
    .map((t) => (t ? new Date(t).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  if (timestamps.length === 0) return null;
  const lastGenerated = Math.max(...timestamps);
  const reviewedAt = ex?.admin_reviewed_at ? new Date(ex.admin_reviewed_at).getTime() : null;
  if (reviewedAt && reviewedAt >= lastGenerated) return 'reviewed';
  return 'draft';
};

export const ReviewStatusBadge = ({ exercise, en }) => {
  const status = getReviewStatus(exercise);
  if (!status) return null;
  if (status === 'reviewed') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/30" data-testid="review-badge-reviewed">
        {en ? 'Reviewed' : 'Relu'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30" data-testid="review-badge-draft">
      {en ? 'AI draft' : 'Brouillon IA'}
    </span>
  );
};

// Badge de confiance client — visible pour TOUS (pas juste l'admin) quand la vidéo de
// l'exercice a bien été générée et validée par le pipeline IA (Sora 2 + audit qualité).
export const VerifiedBadge = ({ en }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" data-testid="ai-verified-badge">
    <BadgeCheck className="w-2.5 h-2.5" /> {en ? 'AI verified' : 'Vérifié IA'}
  </span>
);

export const NewBadge = ({ en }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/90 text-white" data-testid="new-ai-badge">
    <Sparkles className="w-2.5 h-2.5" /> {en ? 'New (AI)' : 'Nouveau (IA)'}
  </span>
);

export const ExerciseImage = ({ src, alt }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-800/50 text-zinc-500">
        <ImageOff className="w-8 h-8 mb-1" />
        <span className="text-[10px]">image en cours…</span>
      </div>
    );
  }
  return (
    <div className="w-full h-full bg-zinc-950 flex items-center justify-center">
      <img
        src={`${backendUrl}${src}`}
        alt={alt}
        loading="lazy"
        onError={() => setErr(true)}
        className="max-w-full max-h-full w-full h-full object-contain"
      />
    </div>
  );
};

// Média d'un exercice — bascule automatiquement sur la vidéo animée (Sora 2) si disponible,
// aussi bien dans les vignettes de la grille que dans le détail (autoplay muet en boucle).
// `badgePosition="left"` évite que le badge ne passe sous le bouton de fermeture du modal.
export const ExerciseMedia = ({ exercise, alt, testid, badgePosition = 'right', autoPlayVideo = true }) => {
  const [videoError, setVideoError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const hasVideo = exercise?.video_status === 'ready' && exercise?.video_url && !exercise?.video_disabled && !videoError;
  const tryPlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);
  useEffect(() => {
    if (hasVideo && autoPlayVideo) tryPlay();
  }, [hasVideo, autoPlayVideo, exercise?.video_url, tryPlay]);
  const videoBadge = (
    <img
      src="/icon-192.png" alt="Body-Up"
      className={`absolute top-1.5 ${badgePosition === 'left' ? 'left-1.5' : 'right-1.5'} w-6 h-6 rounded-full ring-1 ring-white/60 logo-soft-pulse`}
      data-testid="video-badge"
    />
  );
  const newVideoBadge = isRecentVideo(exercise) && (
    <span className={`absolute ${badgePosition === 'left' ? 'bottom-12' : 'bottom-1.5'} left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/90 text-white animate-pulse`} data-testid="new-video-badge">
      <Sparkles className="w-2.5 h-2.5" /> Nouvelle vidéo
    </span>
  );
  if (hasVideo && !autoPlayVideo) {
    // Grille : on n'anime PAS des dizaines de vidéos en même temps (surcharge réseau mobile —
    // c'est ce qui rendait la lecture peu fiable). Juste la photo + le badge, la vidéo ne se
    // charge qu'à l'ouverture de la fiche détail (une seule à la fois, beaucoup plus fiable).
    return (
      <div className="relative w-full h-full">
        <ExerciseImage src={exercise?.image_url} alt={alt} />
        {videoBadge}
        {newVideoBadge}
      </div>
    );
  }
  if (hasVideo) {
    return (
      <div className="relative w-full h-full" onClick={tryPlay}>
        {/* La vraie photo reste TOUJOURS visible en dessous (fiable à 100%, jamais d'écran
            noir) — la vidéo se superpose en fondu UNIQUEMENT une fois sa lecture confirmée
            (`onPlaying`). Si l'autoplay est bloqué par l'appareil (ex: mode économie d'énergie
            iOS), l'utilisateur ne voit jamais rien de cassé, juste la photo + un bouton ▶. */}
        <ExerciseImage src={exercise?.image_url} alt={alt} />
        <video
          ref={videoRef}
          src={`${backendUrl}${exercise.video_url}`}
          className={`absolute inset-0 w-full h-full object-contain bg-zinc-950 transition-opacity duration-300 ${playing ? 'opacity-100' : 'opacity-0'}`}
          preload="auto" autoPlay loop muted playsInline
          onPlaying={() => setPlaying(true)} onPause={() => setPlaying(false)}
          onError={() => setVideoError(true)} data-testid={testid}
        />
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="video-tap-to-play-overlay">
            <div className="w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Play className="w-5 h-5 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}
        {videoBadge}
        {newVideoBadge}
      </div>
    );
  }
  return <ExerciseImage src={exercise?.image_url} alt={alt} />;
};

export const LevelBadge = ({ level, en }) => {
  const labels = {
    debutant: en ? 'Beginner' : 'Débutant',
    intermediaire: en ? 'Intermediate' : 'Intermédiaire',
    avance: en ? 'Advanced' : 'Avancé',
    pro: 'Pro',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${LEVEL_COLORS[level] || 'bg-zinc-500/15 text-zinc-300'}`}
      data-testid={`badge-level-${level}`}>
      {labels[level] || level}
    </span>
  );
};

export const TypeIcon = ({ type, className = 'w-3 h-3' }) => (
  type === 'cardio'
    ? <Activity className={`${className} text-orange-400 flex-shrink-0`} data-testid="exercise-type-icon-cardio" />
    : <Dumbbell className={`${className} text-sky-400 flex-shrink-0`} data-testid="exercise-type-icon-musculation" />
);

export const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap border ${
      active
        ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent'
        : 'bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-primary/40'
    }`}
  >{children}</button>
);

// --- Like / dislike (retour terrain client, influence le générateur IA) -----
export const RatingButtons = ({ exercise, en, onRate }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const myRating = exercise.my_rating;
  const likes = exercise.likes_count || 0;
  const dislikes = exercise.dislikes_count || 0;
  return (
    <div className="flex items-center gap-2" data-testid="rating-buttons">
      <button
        onClick={() => onRate(exercise.id, 1)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
          myRating === 1 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-zinc-800/60 text-zinc-400 border-white/10 hover:border-emerald-500/30'
        }`}
        data-testid="like-btn"
      >
        <ThumbsUp className="w-3.5 h-3.5" fill={myRating === 1 ? 'currentColor' : 'none'} /> {likes}
      </button>
      <button
        onClick={() => onRate(exercise.id, -1)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
          myRating === -1 ? 'bg-red-500/20 text-red-300 border-red-500/40' : 'bg-zinc-800/60 text-zinc-400 border-white/10 hover:border-red-500/30'
        }`}
        data-testid="dislike-btn"
      >
        <ThumbsDown className="w-3.5 h-3.5" fill={myRating === -1 ? 'currentColor' : 'none'} /> {dislikes}
      </button>
      <span className="text-[10px] text-zinc-500 italic hidden sm:inline">
        {L('Votre avis affine les futures suggestions IA', 'Your feedback refines future AI suggestions')}
      </span>
    </div>
  );
};

// --- Modal détail exercice ---------------------------------------------------
export const VIDEO_FEEDBACK_QUICK_TAGS = [
  { fr: 'Membre manquant ou en double', en: 'Missing or duplicated limb' },
  { fr: 'Mouvement incohérent avec l\'exercice', en: 'Movement inconsistent with the exercise' },
  { fr: 'Position de départ incorrecte', en: 'Incorrect starting position' },
  { fr: 'Anatomie/visage déformé', en: 'Distorted anatomy/face' },
];

export const ExerciseDetailModal = ({ exercise, en, onClose, isAdmin, onDelete, onGenerateVideo, onDeleteVideo, videoState, onRate, authFetch, onLocalUpdate }) => {
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmDelVideo, setConfirmDelVideo] = useState(false);
  const [videoFeedback, setVideoFeedback] = useState('');
  useEffect(() => {
    if (!exercise) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [exercise, onClose]);
  useEffect(() => { setVideoFeedback(''); }, [exercise?.id]);
  if (!exercise) return null;
  const L = (fr, eng) => (en ? eng : fr);
  const vState = videoState || { status: exercise.video_status === 'failed' ? 'failed' : undefined, failReason: exercise.video_fail_reason };
  const isGeneratingVideo = vState.status === 'generating';
  const imageReady = exercise.image_status === 'ready';
  const toggleFeedbackTag = (tag) => {
    const label = en ? tag.en : tag.fr;
    setVideoFeedback((prev) => (prev.includes(label) ? prev : (prev ? `${prev}. ${label}` : label)));
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose} data-testid="exercise-detail-modal">
      <div
        className="bg-gradient-to-br from-zinc-900 to-black border border-primary/30 rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video bg-zinc-950 overflow-hidden">
          {isGeneratingVideo ? (
            <ExerciseImage src={exercise.image_url} alt={exercise.name_fr} />
          ) : (
            <ExerciseMedia exercise={exercise} alt={exercise.name_fr} testid="exercise-detail-video" badgePosition="left" />
          )}
          <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black" data-testid="modal-close-btn">
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-3 flex gap-2 flex-wrap">
            <LevelBadge level={exercise.level} en={en} />
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/70 text-white border border-white/10">
              {(en ? EQUIPMENT_LABEL_EN : EQUIPMENT_LABEL_FR)[exercise.equipment] || exercise.equipment}
            </span>
            {exercise.video_status === 'ready' && <VerifiedBadge en={en} />}
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <p className="text-xs text-primary font-bold uppercase tracking-wide">{exercise.muscle_group}</p>
            <h3 className="text-2xl font-black text-white mt-1 flex items-center gap-2" data-testid="modal-exercise-name">
              {exercise.name_fr}
              {isRecentAiExercise(exercise) && <NewBadge en={en} />}
            </h3>
            {exercise.name_en && exercise.name_en !== exercise.name_fr && (
              <p className="text-xs text-zinc-500 mt-0.5 italic">{exercise.name_en}</p>
            )}
          </div>
          <RatingButtons exercise={exercise} en={en} onRate={onRate} />
          {exercise.muscle_secondary && exercise.muscle_secondary.length > 0 && (
            <div className="text-xs text-zinc-400">
              <span className="font-bold text-zinc-300">{L('Muscles secondaires : ', 'Secondary : ')}</span>
              {exercise.muscle_secondary.join(', ')}
            </div>
          )}
          {exercise.execution && (
            <div>
              <p className="text-xs font-bold text-zinc-400 uppercase mb-1">{L('Exécution', 'Execution')}</p>
              <p className="text-sm text-zinc-300 leading-relaxed">{exercise.execution}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-zinc-800/50 border border-white/10 p-3" data-testid="stat-positive-reviews">
              <p className="text-[10px] text-zinc-500 font-bold uppercase">{L("% d'avis positifs", 'Positive reviews')}</p>
              <p className="text-white font-bold mt-1">
                {(exercise.likes_count || 0) + (exercise.dislikes_count || 0) > 0
                  ? `${Math.round((exercise.likes_count || 0) / ((exercise.likes_count || 0) + (exercise.dislikes_count || 0)) * 100)}% (${(exercise.likes_count || 0) + (exercise.dislikes_count || 0)})`
                  : L('Pas encore d\'avis', 'No reviews yet')}
              </p>
            </div>
            <div className="rounded-xl bg-zinc-800/50 border border-white/10 p-3" data-testid="stat-exercise-type">
              <p className="text-[10px] text-zinc-500 font-bold uppercase">{L('Type', 'Type')}</p>
              <p className="text-white font-bold mt-1 flex items-center gap-1.5">
                <TypeIcon type={exercise.type} className="w-4 h-4" />
                {exercise.type === 'cardio' ? L('Cardio', 'Cardio') : L('Musculation', 'Strength')}
              </p>
            </div>
          </div>
          {exercise.tips && exercise.tips.length > 0 && (
            <div>
              <p className="text-xs font-bold text-zinc-400 uppercase mb-2">{L('Conseils clés', 'Key tips')}</p>
              <ul className="space-y-1 text-sm text-zinc-300">
                {exercise.tips.map((t, i) => (
                  <li key={i} className="flex gap-2"><span className="text-primary">•</span>{t}</li>
                ))}
              </ul>
            </div>
          )}

          {isAdmin && (
            <div className="pt-4 border-t border-white/10 space-y-3" data-testid="admin-actions">
              <p className="text-xs font-bold text-primary uppercase">{L('Actions admin', 'Admin actions')}</p>
              <ExerciseAdminTools exercise={exercise} en={en} authFetch={authFetch} onLocalUpdate={onLocalUpdate} />

              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer pt-2 border-t border-white/5" data-testid="video-disabled-toggle-label">
                <input type="checkbox" checked={!!exercise.video_disabled}
                  onChange={async (e) => {
                    const video_disabled = e.target.checked;
                    try {
                      const res = await authFetch(`/api/exercises/${exercise.id}`, { method: 'PATCH', body: JSON.stringify({ video_disabled }) });
                      if (!res.ok) throw new Error();
                      onLocalUpdate({ video_disabled });
                      toast.success(video_disabled
                        ? L('Vidéo désactivée — image seule affichée aux clients', 'Video disabled — image only shown to clients')
                        : L('Vidéo réactivée pour cet exercice', 'Video re-enabled for this exercise'));
                    } catch { toast.error(L('Échec de la mise à jour', 'Failed to update')); }
                  }}
                  className="w-4 h-4 accent-fuchsia-500" data-testid="video-disabled-checkbox" />
                {L('Mode image seule (désactive la vidéo pour cet exercice)', 'Image-only mode (disables video for this exercise)')}
              </label>

              <div className="pt-2 border-t border-white/5 space-y-2">
                {exercise.video_disabled && (
                  <p className="text-[11px] text-amber-400 italic" data-testid="video-disabled-hint">
                    {L("La vidéo est désactivée pour cet exercice — seule l'image est affichée aux clients. Décochez la case ci-dessus pour réactiver.",
                       'Video is disabled for this exercise — only the image is shown to clients. Uncheck the box above to re-enable.')}
                  </p>
                )}
                {!exercise.video_disabled && !isGeneratingVideo && (
                  <div className="space-y-1.5" data-testid="video-feedback-section">
                    <p className="text-[11px] font-bold text-zinc-400 uppercase">
                      {L('Corriger la prochaine génération (optionnel)', 'Fix the next generation (optional)')}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {VIDEO_FEEDBACK_QUICK_TAGS.map((tag, i) => (
                        <button key={i} type="button" onClick={() => toggleFeedbackTag(tag)}
                          className="px-2 py-1 rounded-full text-[10px] font-bold bg-zinc-800 text-zinc-300 border border-white/10 hover:border-fuchsia-500/50"
                          data-testid={`video-feedback-tag-${i}`}>
                          {en ? tag.en : tag.fr}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={videoFeedback}
                      onChange={(e) => setVideoFeedback(e.target.value)}
                      placeholder={L(
                        "Ex : le bras droit disparaît à mi-mouvement, il faut garder les 2 bras visibles tout le long...",
                        'Ex: the right arm disappears mid-movement, keep both arms visible throughout...'
                      )}
                      className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2 text-xs text-white min-h-[60px]"
                      data-testid="video-feedback-input"
                    />
                    {exercise.video_last_feedback && (
                      <p className="text-[10px] text-zinc-500 italic" data-testid="video-last-feedback-hint">
                        {L('Dernière correction demandée : ', 'Last correction requested: ')}"{exercise.video_last_feedback}"
                      </p>
                    )}
                  </div>
                )}
                <button
                  onClick={() => onGenerateVideo(exercise.id, videoFeedback)}
                  disabled={isGeneratingVideo || !imageReady || exercise.video_disabled}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-fuchsia-600 to-indigo-600 disabled:opacity-50"
                  data-testid="regen-video-btn"
                >
                  {isGeneratingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
                  {isGeneratingVideo
                    ? L('Génération en cours…', 'Generating…')
                    : L('Générer version animée (Sora 2)', 'Generate animated version (Sora 2)')}
                </button>
                {exercise.video_status === 'ready' && !isGeneratingVideo && (
                  !confirmDelVideo ? (
                    <button onClick={() => setConfirmDelVideo(true)}
                      className="inline-flex items-center gap-1.5 ml-2 px-3 py-2 rounded-lg text-sm font-bold text-red-400 border border-red-500/30 hover:bg-red-500/10"
                      data-testid="delete-video-btn">
                      <Trash2 className="w-4 h-4" /> {L('Supprimer la vidéo', 'Delete video')}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 ml-2">
                      <span className="text-xs text-red-300">{L('Garder uniquement l\'image ?', 'Keep image only?')}</span>
                      <button onClick={() => { onDeleteVideo(exercise.id); setConfirmDelVideo(false); }}
                        className="px-2.5 py-1 rounded text-xs font-bold text-white bg-red-500"
                        data-testid="delete-video-confirm-btn">
                        {L('Oui, supprimer', 'Yes, delete')}
                      </button>
                      <button onClick={() => setConfirmDelVideo(false)}
                        className="px-2.5 py-1 rounded text-xs font-bold text-zinc-300 bg-zinc-800">
                        {L('Annuler', 'Cancel')}
                      </button>
                    </span>
                  )
                )}
                <p className="text-[11px] text-zinc-500 italic" data-testid={!imageReady ? "video-blocked-hint" : "video-status-hint"}>
                  {!imageReady
                    ? L("Générez d'abord une image pour cet exercice avant la vidéo.", 'Generate an image for this exercise first before the video.')
                    : isGeneratingVideo
                    ? L('Sora 2 génère un clip de 4s (2-5 min, coût IA plus élevé). Vous pouvez fermer cette fenêtre, le statut est sauvegardé.',
                        'Sora 2 is generating a 4s clip (2-5 min, higher AI cost). You can close this window, status is saved.')
                    : vState.status === 'failed' && vState.failReason === 'moderation_blocked'
                    ? L('Bloqué par la modération du fournisseur (contenu jugé sensible) — inutile de relancer à l\'identique, ajustez le texte d\'exécution ou le feedback ci-dessus avant de réessayer.',
                        'Blocked by provider moderation (content flagged as sensitive) — no point relaunching as-is, adjust the execution text or feedback above first.')
                    : vState.status === 'failed' && vState.interrupted
                    ? L('Génération interrompue (redémarrage serveur) — pas un vrai échec, relancez.', 'Generation interrupted (server restart) — not a real failure, relaunch.')
                    : vState.status === 'failed'
                    ? L('Dernière génération vidéo échouée — réessayez, ou décrivez le problème ci-dessus.', 'Last video generation failed — try again, or describe the issue above.')
                    : L("Le clip animé remplace l'illustration statique, dans le détail ET sur la vignette de la grille.",
                        "The animated clip replaces the static illustration, in the detail view AND on the grid thumbnail.")}
                </p>
              </div>

              {!confirmDel ? (
                <button onClick={() => setConfirmDel(true)}
                  className="text-xs text-red-400 hover:text-red-300 font-bold"
                  data-testid="delete-exercise-btn">
                  {L('Supprimer cet exercice', 'Delete this exercise')}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-red-300">{L('Confirmer la suppression ?', 'Confirm delete?')}</p>
                  <button onClick={() => onDelete(exercise.id)}
                    className="px-2.5 py-1 rounded text-xs font-bold text-white bg-red-500"
                    data-testid="delete-confirm-btn">
                    {L('Oui, supprimer', 'Yes, delete')}
                  </button>
                  <button onClick={() => setConfirmDel(false)}
                    className="px-2.5 py-1 rounded text-xs font-bold text-zinc-300 bg-zinc-800">
                    {L('Annuler', 'Cancel')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Autocomplete search box -------------------------------------------------
export const SearchBox = ({ authFetch, en, onPick }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authFetch(`/api/exercises/search?q=${encodeURIComponent(q.trim())}`);
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, authFetch]);

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder={en ? 'Search an exercise (2+ letters)…' : 'Rechercher un exercice (2+ lettres)…'}
          className="w-full bg-zinc-900 border border-white/10 rounded-xl pl-10 pr-10 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-primary focus:outline-none"
          data-testid="library-search-input"
        />
        {q && (
          <button onClick={() => { setQ(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="library-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-zinc-900 border border-white/10 rounded-xl shadow-2xl max-h-80 overflow-y-auto" data-testid="library-search-results">
          {results.map((r) => (
            <button key={r.id} onClick={() => { setOpen(false); setQ(''); onPick(r.id); }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-800 border-b border-white/5 text-left"
              data-testid={`search-result-${r.id}`}>
              <div className="w-10 h-10 rounded overflow-hidden bg-zinc-800 flex-shrink-0">
                <ExerciseImage src={r.image_url} alt={r.name_fr} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{r.name_fr}</p>
                <p className="text-[10px] text-zinc-500">{r.muscle_group} · {r.level}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Widget "Solde LLM estimé" (admin) — l'Emergent Universal Key n'a pas d'API de lecture du
 * solde réel : l'admin synchronise donc manuellement le solde vu sur son tableau de bord
 * Emergent, et l'app décrémente ensuite une estimation à chaque génération IA coûteuse. */
export const LlmBudgetWidget = ({ en, budget, authFetch, onUpdated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (!budget) return null;
  const low = budget.low;

  const save = async () => {
    const n = parseFloat(value);
    if (Number.isNaN(n) || n < 0) return;
    setSaving(true);
    try {
      const r = await authFetch('/api/exercises/admin/llm-budget', { method: 'PUT', body: JSON.stringify({ balance_chf: n }) });
      const d = await r.json();
      onUpdated(d);
      setOpen(false);
    } catch { /* silencieux, le widget garde l'ancienne valeur affichée */ }
    setSaving(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setValue(budget.balance_chf != null ? String(budget.balance_chf) : ''); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
          low ? 'bg-red-500/15 text-red-300 border-red-500/40 animate-pulse' : 'bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-primary/40'
        }`}
        data-testid="llm-budget-widget-btn"
      >
        {low ? <AlertTriangle className="w-3.5 h-3.5" /> : <Wallet className="w-3.5 h-3.5" />}
        {budget.balance_chf != null ? `${budget.balance_chf.toFixed(2)} CHF` : L('Solde LLM ?', 'LLM balance ?')}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 w-64 rounded-xl bg-zinc-900 border border-white/10 shadow-2xl p-3.5" data-testid="llm-budget-editor">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{L('Synchroniser le solde (CHF)', 'Sync balance (CHF)')}</p>
            <p className="text-[11px] text-zinc-500 mb-2">{L('Valeur vue sur votre tableau de bord Emergent (Profil > Gérer le forfait > Clé universelle). Alerte sous ', 'Value seen on your Emergent dashboard (Profile > Manage plan > Universal key). Alert below ')}{budget.threshold_chf} CHF.</p>
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)}
                className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-primary/50"
                data-testid="llm-budget-input" />
              <button onClick={save} disabled={saving} className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 disabled:opacity-50" data-testid="llm-budget-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </button>
            </div>
            {budget.updated_at && (
              <p className="text-[10px] text-zinc-600 mt-2">{L('Mis à jour', 'Updated')} {new Date(budget.updated_at).toLocaleDateString()}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/** Modal "Historique des audits de posture" (admin) — liste des campagnes passées avec date,
 * écarts trouvés et images régénérées. */
export const AuditHistoryModal = ({ en, open, onClose, history, loading }) => {
  const L = (fr, eng) => (en ? eng : fr);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="audit-history-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><History className="w-5 h-5 text-orange-400" /> {L('Historique des audits de posture', 'Posture audit history')}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="audit-history-close"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8">{L('Aucun audit lancé pour le moment.', 'No audit launched yet.')}</p>
        ) : (
          <div className="space-y-2.5" data-testid="audit-history-list">
            {history.map((h, i) => (
              <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid={`audit-history-item-${i}`}>
                <p className="text-xs text-zinc-500 mb-1.5">{new Date(h.started_at).toLocaleString()}</p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-zinc-300">{L('Vérifiés', 'Checked')} : <b className="text-white">{h.checked}</b></span>
                  <span className="text-orange-300">{L('Écarts', 'Mismatches')} : <b>{h.mismatches}</b></span>
                  <span className="text-emerald-300">{L('Régénérées', 'Regenerated')} : <b>{h.regenerated}</b></span>
                  {h.errors > 0 && <span className="text-red-400">{L('Erreurs', 'Errors')} : <b>{h.errors}</b></span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

